import { ARTICLE_GROUND } from '../../util/article-ground';
import type { ReactNode } from 'react';
import { Fragment } from 'react';
import { cn } from '@rocco/utils/cn';
import { FlexibleModuleEnum, TextTypeEnum } from 'generated/graphql';
import type { PageBuilderModuleType } from '@modules/page-builder/types';
import { RevampRichText } from './RevampRichText';
import { RevampCellRow, LANDSCAPE_CELL_REM } from './RevampProseRow';
import { RevampImageFigure } from './RevampImageFigure';
import { RevampQuoteModule } from './RevampQuoteModule';
import { RevampSloganModule } from './RevampSloganModule';
import { RevampGalleryModule } from './RevampGalleryModule';
import { RevampCollectionModule } from './RevampCollectionModule';
import { RevampProfessionalCtas } from './RevampProfessionalCtas';
import { RevampVideoModule } from './RevampVideoModule';
import { RevampModuleSection } from './RevampModuleSection';
import {
    revampDuoCellWidths,
    isLandscapeModule,
    isMediumSingleImage,
    REVAMP_CELL_LANDSCAPE,
    REVAMP_LANDSCAPE_LARGE,
    REVAMP_PORTRAIT_LARGE,
    REVAMP_PORTRAIT_SMALL,
} from '../../util/article-image-geometry';
import {
    REVAMP_IMAGE_COLUMN_SIZES,
    REVAMP_IMAGE_SPREAD_SIZES,
    REVAMP_IMAGE_WIDE_SIZES,
} from '../../util/revamp-image-sizes';
import { REVAMP_STANDALONE_HEADING } from '../../util/article-prose';

/** How many body modules remain visible when article auth gating is on. */
const GATED_MODULE_PREVIEW_COUNT = 2;

type RevampProfessional = { title: string; link: string };

/**
 * The picture the body opens beside when its first module brings none of
 * its own: the article's banner, repeated in black and white.
 */
export interface RevampOpenerImage {
    src: string;
    alt?: string;
    caption?: string;
}

interface RevampModulesProps {
    modules: PageBuilderModuleType[];
    professional?: RevampProfessional;
    openerImage?: RevampOpenerImage;
    /** Truncate and blur body modules for unauthenticated gated readers. */
    articleGatingEnabled?: boolean;
    /**
     * Slotted in below the first module that actually renders — the first
     * treatment the reader sees, skipping any module with no revamp renderer.
     */
    afterFirstModule?: ReactNode;
}

/** The landscape component's own margins: 200px each side at 1920. */
const LANDSCAPE_INSET = 'md:px-[12.5rem]';

/** The small portrait's width in its cell. */
const PORTRAIT_SMALL_WIDTH = 'md:w-[27.375rem]';

/**
 * Every article opens beside a portrait. Where the first run of copy is a
 * paragraph module with no picture of its own, the banner stands in on the
 * left — as "Image / portrait / small", in black and white so it reads as
 * the article's own opening rather than the banner shown twice.
 */
const OpenerPortrait = ({ image }: { image: RevampOpenerImage }) => (
    <RevampImageFigure
        column={{
            id: 'opener-portrait',
            formId: 'opener-portrait',
            imageUrl: image.src,
            caption: image.caption,
            imageIsPortrait: true,
            imageAspectRatio: 438 / 615,
            dimension: {
                id: 0,
                width: 438,
                height: 615,
                tabletWidth: 438,
                tabletHeight: 615,
                mobileWidth: 438,
                mobileHeight: 615,
                isPortrait: true,
            },
        }}
        sizes={REVAMP_IMAGE_SPREAD_SIZES}
        className={PORTRAIT_SMALL_WIDTH}
        aspectRatio={REVAMP_PORTRAIT_SMALL}
        imgClassName="grayscale"
    />
);

const renderModule = (
    module: PageBuilderModuleType,
    professional?: RevampProfessional,
    /** This module opens the body, so its first words carry the lede's bold. */
    leadIn = false,
    /** The banner, for an opening paragraph with no picture beside it. */
    openerImage?: RevampOpenerImage,
) => {
    const columns = module.columns;
    const firstColumn = columns[0];
    if (!firstColumn) return null;
    const opener =
        leadIn && openerImage ? (
            <OpenerPortrait image={openerImage} />
        ) : undefined;

    switch (module.presetType) {
        case FlexibleModuleEnum.HeadingModule:
            // The heading of the paragraph component, standing alone: in the
            // right cell, where the copy it titles will follow. No trailing
            // margin — the break to the next module is the heading's own 35px.
            return (
                <RevampCellRow
                    right={
                        <RevampRichText
                            contentHTML={firstColumn.contentHTML}
                            className={REVAMP_STANDALONE_HEADING}
                        />
                    }
                />
            );
        case FlexibleModuleEnum.SingleParagraphModule:
            // "Paragraph / single column", in the right cell: the left is
            // where a picture or a quote stands beside it, and stays its
            // width when nothing does.
            return (
                <RevampCellRow
                    left={opener}
                    right={
                        <RevampRichText
                            contentHTML={firstColumn.contentHTML}
                            leadIn={leadIn}
                        />
                    }
                />
            );
        case FlexibleModuleEnum.DuoParagraphModule:
            // "Paragraph / double column": two 438px columns on a 24px gutter
            // inside the right cell.
            return (
                <RevampCellRow
                    left={opener}
                    right={
                        <div className="flex w-full flex-col gap-6 md:flex-row md:gap-[1.5rem]">
                            {columns.slice(0, 2).map((column, index) => (
                                <RevampRichText
                                    key={column.id}
                                    contentHTML={column.contentHTML}
                                    className="min-w-0 flex-1 md:[&_p]:max-w-none"
                                    leadIn={leadIn && index === 0}
                                />
                            ))}
                        </div>
                    }
                />
            );
        // Optional title (the module's h3 column) over the LEFT pair of four
        // equal text columns; the right pair top-aligns with the title. Each
        // pair is a "double column" in its own cell. Empty columns are
        // dropped so the remaining ones keep equal widths.
        case FlexibleModuleEnum.QuadParagraphModule: {
            const titleColumn = columns.find(
                (column) =>
                    column.textType === TextTypeEnum.H3 && column.contentHTML,
            );
            const paragraphColumns = columns
                .filter((column) => column.textType !== TextTypeEnum.H3)
                .slice(0, 4)
                .filter((column) => column.contentHTML);
            const leftPair = paragraphColumns.slice(0, 2);
            const rightPair = paragraphColumns.slice(2, 4);
            const pair = (pairColumns: typeof columns) => (
                <div className="flex w-full flex-col gap-6 md:flex-row md:gap-[1.5rem]">
                    {pairColumns.map((column) => (
                        <RevampRichText
                            key={column.id}
                            contentHTML={column.contentHTML}
                            className="min-w-0 flex-1 md:[&_p]:max-w-none"
                        />
                    ))}
                </div>
            );
            return (
                <RevampCellRow
                    left={
                        <div className="flex w-full flex-col">
                            {titleColumn && (
                                <RevampRichText
                                    contentHTML={titleColumn.contentHTML}
                                    promoteHeading
                                    className="w-full"
                                />
                            )}
                            {pair(leftPair)}
                        </div>
                    }
                    right={rightPair.length > 0 ? pair(rightPair) : undefined}
                />
            );
        }
        case FlexibleModuleEnum.QuoteModule:
            return (
                <RevampQuoteModule
                    column={firstColumn}
                    professional={professional}
                />
            );
        case FlexibleModuleEnum.SloganModule:
            // "Quote / half", in the left cell — where the article's own
            // layout puts it, with the copy it opens in the right.
            return (
                <RevampCellRow
                    left={
                        <RevampSloganModule
                            column={firstColumn}
                            professional={professional}
                            variant="half"
                        />
                    }
                />
            );
        case FlexibleModuleEnum.FullWidthSloganModule:
            return (
                <RevampSloganModule
                    column={firstColumn}
                    professional={professional}
                    variant="fullWidth"
                />
            );
        // "Quote / half" beside "Paragraph / single column": the quote in the
        // left cell, the copy — with its optional title — in the right. Both
        // text columns are paragraphs, so sort order tells the slogan from
        // the body.
        case FlexibleModuleEnum.SloganParagraphModule: {
            const titleColumn = columns.find(
                (column) =>
                    column.textType === TextTypeEnum.H3 && column.contentHTML,
            );
            const [sloganColumn, bodyColumn] = columns.filter(
                (column) => column.textType !== TextTypeEnum.H3,
            );
            if (!sloganColumn?.contentHTML && !bodyColumn?.contentHTML) {
                return null;
            }
            return (
                <RevampCellRow
                    left={
                        sloganColumn ? (
                            <RevampSloganModule
                                column={sloganColumn}
                                professional={professional}
                                variant="half"
                            />
                        ) : undefined
                    }
                    right={
                        <div className="min-w-0">
                            {titleColumn && (
                                <RevampRichText
                                    contentHTML={titleColumn.contentHTML}
                                    promoteHeading
                                />
                            )}
                            {bodyColumn && (
                                <RevampRichText
                                    contentHTML={bodyColumn.contentHTML}
                                />
                            )}
                        </div>
                    }
                />
            );
        }
        case FlexibleModuleEnum.SingleImageModule:
            if (isLandscapeModule(module) && isMediumSingleImage(module)) {
                // "Single medium photo" is the same 3:2 crop taken to the
                // body's own cell instead of the page's 1520 margins. Drawn
                // on those margins it was the large picture exactly, and the
                // two presets were indistinguishable.
                return (
                    <RevampCellRow
                        left={
                            <RevampImageFigure
                                column={firstColumn}
                                sizes={REVAMP_IMAGE_COLUMN_SIZES}
                                aspectRatio={REVAMP_CELL_LANDSCAPE}
                            />
                        }
                    />
                );
            }
            return isLandscapeModule(module) ? (
                // "Image / landscape / large": 1520x800 on the page's 200px
                // margins, the description under it on the design's 400.
                // px-4.5 keeps it in the phone's prose gutter, as the video
                // module below does — bled to the edge, its rounded corners
                // had nothing to sit on.
                <RevampImageFigure
                    column={firstColumn}
                    sizes={REVAMP_IMAGE_WIDE_SIZES}
                    className={cn('px-4.5', LANDSCAPE_INSET)}
                    aspectRatio={REVAMP_LANDSCAPE_LARGE}
                />
            ) : (
                // "Image / portrait / large" in the left cell, the right left
                // empty: a lone portrait lines up with a pair of them rather
                // than being its own width.
                <RevampCellRow
                    left={
                        <RevampImageFigure
                            column={firstColumn}
                            sizes={REVAMP_IMAGE_COLUMN_SIZES}
                            aspectRatio={REVAMP_PORTRAIT_LARGE}
                        />
                    }
                />
            );
        case FlexibleModuleEnum.DuoImageModule: {
            // Filtered, not just sliced: a duo whose second column carries no
            // image renders that column as nothing. Count the images, not
            // the columns.
            const pair = columns
                .filter((column) => column.imageUrl)
                .slice(0, 2);
            // Two "portrait / large" cells side by side, or two landscapes
            // taken to the cells' width, the pair spanning what the single
            // landscape picture spans. One image is the pair with the right
            // side left empty.
            const ratio = isLandscapeModule(module)
                ? REVAMP_CELL_LANDSCAPE
                : REVAMP_PORTRAIT_LARGE;
            const figures = pair.map((column) => (
                <RevampImageFigure
                    key={column.id}
                    column={column}
                    sizes={REVAMP_IMAGE_COLUMN_SIZES}
                    aspectRatio={ratio}
                />
            ));
            // An asymmetric pair states its asymmetry in the stored widths.
            // Without this the row's equal cells squared it up and the preset
            // rendered as the symmetrical one it exists to differ from.
            return (
                <RevampCellRow
                    width="landscape"
                    left={figures[0]}
                    right={figures[1]}
                    cellWidths={revampDuoCellWidths(pair, LANDSCAPE_CELL_REM)}
                />
            );
        }
        case FlexibleModuleEnum.ImageParagraphModule: {
            const imageColumn = columns.find((column) => column.imageUrl);
            const titleColumn = columns.find(
                (column) =>
                    column.textType === TextTypeEnum.H3 && column.contentHTML,
            );
            const bodyColumns = columns.filter(
                (column) =>
                    column.textType !== TextTypeEnum.H3 && column.contentHTML,
            );
            const showCtaButtons = columns.some(
                (column) => column.showCtaButtons,
            );
            if (!imageColumn && !titleColumn && bodyColumns.length === 0) {
                return null;
            }
            // "Image / portrait / small" in the left cell — 438x615, its
            // description and the CTA pair under it — beside "Paragraph /
            // single column" in the right.
            return (
                <RevampCellRow
                    left={
                        imageColumn ? (
                            // "Image toward the centre" parks the 438 picture
                            // against the right of its cell, so it meets the
                            // copy on the row's gutter instead of the page
                            // margin. The editor offers the toggle; without
                            // this it wrote a flag nothing read.
                            <div
                                className={cn(
                                    'flex w-full flex-col gap-6',
                                    imageColumn.alignRight && 'md:items-end',
                                )}
                            >
                                <RevampImageFigure
                                    column={imageColumn}
                                    sizes={REVAMP_IMAGE_SPREAD_SIZES}
                                    className={PORTRAIT_SMALL_WIDTH}
                                    aspectRatio={REVAMP_PORTRAIT_SMALL}
                                />
                                {showCtaButtons && professional && (
                                    <RevampProfessionalCtas
                                        professional={professional}
                                        placement="imageParagraph"
                                    />
                                )}
                            </div>
                        ) : undefined
                    }
                    right={
                        <div className="min-w-0">
                            {titleColumn && (
                                <RevampRichText
                                    contentHTML={titleColumn.contentHTML}
                                    promoteHeading
                                />
                            )}
                            {/* "Photo and two paragraphs" is a spread: its two
                                body columns stand as the cell's own halves on
                                the row's gutter, which is what the preset
                                seeds a second column for. Run together in one
                                column they read as the single-paragraph
                                preset with longer copy. */}
                            <div
                                className={cn(
                                    'flex min-w-0 flex-col gap-6',
                                    bodyColumns.length > 1 && 'md:flex-row',
                                )}
                            >
                                {bodyColumns.map((column, index) => (
                                    <RevampRichText
                                        key={column.id}
                                        contentHTML={column.contentHTML}
                                        className="min-w-0 flex-1"
                                        leadIn={leadIn && index === 0}
                                    />
                                ))}
                            </div>
                        </div>
                    }
                />
            );
        }
        case FlexibleModuleEnum.TrioImageModule:
        case FlexibleModuleEnum.QuadImageModule:
        case FlexibleModuleEnum.OctaImageModule: {
            // Optional ruled heading (the module's h3 column). Only the
            // article-only "…with title" presets carry one; the shared gallery
            // presets have images alone and keep their bare grid.
            const titleHTML = columns.find(
                (column) =>
                    column.textType === TextTypeEnum.H3 && column.contentHTML,
            )?.contentHTML;
            const tileColumns = columns.filter(
                (column) => column.textType !== TextTypeEnum.H3,
            );
            // Collection presets carry products; plain galleries are images.
            if (tileColumns.some((column) => column.productId)) {
                return (
                    <RevampCollectionModule
                        columns={tileColumns}
                        titleHTML={titleHTML}
                    />
                );
            }
            const perRow =
                module.presetType === FlexibleModuleEnum.TrioImageModule
                    ? 3
                    : 4;
            return (
                <RevampGalleryModule
                    columns={tileColumns}
                    perRow={perRow}
                    titleHTML={titleHTML}
                />
            );
        }
        case FlexibleModuleEnum.SingleVideoModule:
            // On the landscape component's box, so a film is one of the
            // article's figures.
            return (
                <RevampVideoModule
                    column={firstColumn}
                    className={cn('px-4.5', LANDSCAPE_INSET)}
                />
            );
        // The film in the left cell, the copy it belongs to in the right —
        // the image + paragraph spread with a video in place of the picture.
        // Without this the module rendered as nothing: "Video and paragraph"
        // is a seeded preset offered on articles, so an author could write
        // one, see it in the builder, and have readers get a gap.
        case FlexibleModuleEnum.VideoParagraphModule: {
            const videoColumn = columns.find(
                (column) => column.video?.videoURL,
            );
            const titleColumn = columns.find(
                (column) =>
                    column.textType === TextTypeEnum.H3 && column.contentHTML,
            );
            const bodyColumns = columns.filter(
                (column) =>
                    column.textType !== TextTypeEnum.H3 && column.contentHTML,
            );
            if (!videoColumn && !titleColumn && bodyColumns.length === 0) {
                return null;
            }
            return (
                <RevampCellRow
                    left={
                        videoColumn ? (
                            <RevampVideoModule column={videoColumn} />
                        ) : undefined
                    }
                    right={
                        <div className="min-w-0">
                            {titleColumn && (
                                <RevampRichText
                                    contentHTML={titleColumn.contentHTML}
                                    promoteHeading
                                />
                            )}
                            {/* "Photo and two paragraphs" is a spread: its two
                                body columns stand as the cell's own halves on
                                the row's gutter, which is what the preset
                                seeds a second column for. Run together in one
                                column they read as the single-paragraph
                                preset with longer copy. */}
                            <div
                                className={cn(
                                    'flex min-w-0 flex-col gap-6',
                                    bodyColumns.length > 1 && 'md:flex-row',
                                )}
                            >
                                {bodyColumns.map((column, index) => (
                                    <RevampRichText
                                        key={column.id}
                                        contentHTML={column.contentHTML}
                                        className="min-w-0 flex-1"
                                        leadIn={leadIn && index === 0}
                                    />
                                ))}
                            </div>
                        </div>
                    }
                />
            );
        }
        // HTML and invoice modules keep no revamp treatment yet — follow-up
        // under BC-4720 once the design covers them.
        default:
            return null;
    }
};

/**
 * Renders the article's page-builder modules with the revamp's editorial
 * treatments. Same CMS data as the legacy DetailModules — only the renderers
 * differ. Self-hides when no module has a revamp treatment. When auth gating
 * is on, only the first two modules render (blurred + faded), matching legacy
 * DetailModules so the sign-in banner can gate the rest of the article.
 */
/**
 * Modules that are running copy rather than a block of their own.
 *
 * The design's 180px between modules is drawn for whole components -- a
 * picture, a quote, a paragraph block. An article's own body is authored at
 * a finer grain, a heading in one module and its paragraphs in the next, and
 * 180px between two of those leaves a heading stranded a screen above the
 * copy it introduces. So two runs of copy stand a paragraph break apart, and
 * a heading stands the paragraph component's own 35px above what it titles.
 */
const PROSE_MODULES = new Set<string>([
    FlexibleModuleEnum.HeadingModule,
    FlexibleModuleEnum.SingleParagraphModule,
    FlexibleModuleEnum.DuoParagraphModule,
    FlexibleModuleEnum.QuadParagraphModule,
]);

/** What a module is, as far as the break above it is concerned. */
interface ModuleBreakKind {
    isProse: boolean;
    isHeading: boolean;
}

/**
 * The break above a module, read off what it and the one above it are.
 *
 * The stack is not evenly spaced: how far a module stands off the one above
 * depends on whether the two are one run of copy, a heading and the copy it
 * titles, or two separate components. The rungs are the same numbers
 * `REVAMP_BODY_PROSE` sets inside a single block, so a run of copy reads the
 * same whether its parts are one module or several.
 */
const moduleBreak = (
    entry: ModuleBreakKind,
    previous: ModuleBreakKind | undefined,
): string => {
    // The first module meets the masthead, which carries its own break.
    if (!previous) return '';
    // The paragraph component's own 35px between its heading and its copy.
    if (previous.isHeading) return 'mt-5 md:mt-[2.1875rem]';
    // A heading opening a new run of copy stands the 90px off it that the
    // prose definition gives a heading typed inline. Without this the same
    // heading sits four times closer to the copy above as a Subheading block
    // than as a typed one, because a heading module is prose too and would
    // otherwise take the paragraph break below.
    if (entry.isHeading && previous.isProse) return 'mt-10 md:mt-[5.625rem]';
    // One paragraph break: the two are one run of copy.
    if (entry.isProse && previous.isProse) return 'mt-6 md:mt-[1.4rem]';
    // The design's 180px between components.
    return 'mt-10 md:mt-[11.25rem]';
};

export const RevampModules = ({
    modules,
    professional,
    openerImage,
    articleGatingEnabled = false,
    afterFirstModule,
}: RevampModulesProps) => {
    const modulesToRender = articleGatingEnabled
        ? modules.slice(0, GATED_MODULE_PREVIEW_COUNT)
        : modules;

    // Where the article starts reading: the first module carrying a run of
    // body copy, whatever kind of module that is. Not the first prose module —
    // an article whose copy opens beside a portrait starts in an
    // image-and-paragraph, which is not in `PROSE_MODULES`.
    const leadInModuleId = modulesToRender.find((module) =>
        module.columns.some(
            (column) =>
                column.textType !== TextTypeEnum.H3 &&
                (column.contentHTML ?? '').replace(/<[^>]*>/g, '').trim() !==
                    '',
        ),
    )?.id;

    const rendered = modulesToRender
        .map((module) => {
            return {
                id: module.id,
                isProse: PROSE_MODULES.has(module.presetType),
                isHeading:
                    module.presetType === FlexibleModuleEnum.HeadingModule,
                // Read progress against the full module list (legacy
                // DetailModules parity), even when gating truncates the render.
                percentContentViewed:
                    (module.memberSortOrder + 1) / modules.length,
                node: renderModule(
                    module,
                    professional,
                    module.id === leadInModuleId,
                    openerImage,
                ),
            };
        })
        .filter((entry) => entry.node !== null);

    if (rendered.length === 0) return null;

    return (
        <div
            className={cn(
                'relative flex w-full flex-col items-center',
                ARTICLE_GROUND,
                articleGatingEnabled &&
                    'max-h-35 overflow-hidden blur-[1px] md:max-h-48',
            )}
        >
            {rendered.map((entry, index) => (
                // Gated previews render immediately — the teaser is clipped and
                // blurred, so a scroll reveal would leave it sitting invisible.
                <Fragment key={entry.id}>
                    <div
                        // Spacing sits on the entry rather than on the
                        // container's gap, because how far a module stands from
                        // the one above depends on what both of them are.
                        className={cn(
                            'w-full',
                            moduleBreak(entry, rendered[index - 1]),
                        )}
                    >
                        <RevampModuleSection
                            moduleId={entry.id}
                            percentContentViewed={entry.percentContentViewed}
                            className="flex w-full justify-center"
                        >
                            {entry.node}
                        </RevampModuleSection>
                    </div>
                    {index === 0 && afterFirstModule && (
                        <div className="mt-10 w-full md:mt-[11.25rem]">
                            {afterFirstModule}
                        </div>
                    )}
                </Fragment>
            ))}
            {articleGatingEnabled && (
                <div className="absolute inset-0 bg-linear-to-b from-gray-50/50 to-gray-50" />
            )}
        </div>
    );
};
